import { inject, Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { faker } from '@faker-js/faker';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import { Vehicle, VehicleCreateDto, VehicleSearchDto } from '../../models/vehicle.interface';
import { ApiResponse } from '@app/core/adaptors/dto';
import { CustomerMocksService } from './customer-mocks.service';
import {
  parametersFromPath,
  parametersFromRequest,
} from '@app/core/interceptors/mock-api.interceptor';

@Injectable({ providedIn: 'root' })
export class VehicleMocksService {
  private readonly STORAGE_KEY = 'mock_vehicles';
  private vehicles: Vehicle[] = [];
  private customerMocksService = inject(CustomerMocksService);

  constructor() {
    this.loadVehiclesFromStorage();
  }

  handleMockRequest(req: HttpRequest<any>) {
    const { tenantId } = parametersFromPath(req, 'vehicles');
    const pathSegments = req.url.split('?')[0].split('/');
    const vehiclesIndex = pathSegments.indexOf('vehicles');
    const idSegment =
      vehiclesIndex !== -1 && pathSegments[vehiclesIndex + 1]
        ? pathSegments[vehiclesIndex + 1]
        : undefined;
    const vehicleId = idSegment ? Number(idSegment) : undefined;

    if (req.method === 'GET' && vehicleId) {
      return this.getVehicleById(vehicleId);
    } else if (req.method === 'GET') {
      const queryParams = parametersFromRequest(req);
      const searchDto = { ...queryParams };
      return this.getVehicles(tenantId, searchDto);
    } else if (req.method === 'POST') {
      const vehicleCreateDto = req.body as VehicleCreateDto;
      return this.saveVehicle(tenantId, vehicleCreateDto);
    } else if (req.method === 'PUT' && vehicleId) {
      const vehicleUpdateDto = req.body as Vehicle;
      return this.updateVehicle(vehicleUpdateDto);
    } else if (req.method === 'DELETE' && vehicleId) {
      return this.deleteVehicleMock(vehicleId);
    } else {
      throw new Error(`Unsupported request: ${req.method} ${req.url}`);
    }
  }

  saveVehicle(tenantId: number, vehicle: VehicleCreateDto): Observable<HttpEvent<any>> {
    const newVehicle: Vehicle = {
      id: this.vehicles.length + 1,
      make: 'Toyota',
      model: 'Highlander',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      tenantId,
      status: 'Active',
      ...vehicle,
    };
    this.vehicles.push(newVehicle);
    this.saveVehiclesToStorage();
    return this.mapToResponse(newVehicle, 201);
  }

  updateVehicle(updatedVehicle: Vehicle) {
    const existingVehicleIndex = this.vehicles.findIndex((v) => v.id === updatedVehicle.id);
    if (existingVehicleIndex !== -1) {
      this.vehicles[existingVehicleIndex] = updatedVehicle;
    }
    this.saveVehiclesToStorage();
    return this.mapToResponse(updatedVehicle, 200);
  }

  getVehicles(tenantId: number, searchCriteria: VehicleSearchDto): Observable<HttpEvent<any>> {
    const { limit, page, query, sortField, sortOrder } = searchCriteria;
    if (this.vehicles.length === 0) {
      this.generateMockVehicles();
      this.saveVehiclesToStorage();
    }

    const filteredData = this.vehicles
      .filter((v) => v.tenantId === tenantId && anyQueryMatch(v, query))
      .sort((a, b) => {
        const aValue = a[sortField as keyof Vehicle];
        const bValue = b[sortField as keyof Vehicle];
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return sortOrder === 'asc'
          ? (aValue as any) - (bValue as any)
          : (bValue as any) - (aValue as any);
      });

    const data = filteredData.slice((page - 1) * limit, page * limit);

    const res: ApiResponse<Vehicle> = {
      data,
      total: filteredData.length,
      limit,
      page,
      totalPages: Math.ceil(filteredData.length / limit),
    };
    console.log('Mock response:', res.limit, res.page, res.total, res.totalPages);
    return this.mapToResponse(res, 200);
  }

  getVehicleById(vehicleId: number): Observable<HttpEvent<any>> {
    const vehicle = this.vehicles.find((v) => v.id === vehicleId);
    if (vehicle) {
      return this.mapToResponse(vehicle, 200);
    } else {
      return this.mapToResponse({ message: 'Vehicle not found' }, 404);
    }
  }

  deleteVehicleMock(vehicleId: number): Observable<HttpEvent<any>> {
    const idx = this.vehicles.findIndex((v) => v.id === vehicleId);
    if (idx !== -1) {
      this.vehicles.splice(idx, 1);
      this.saveVehiclesToStorage();
    }
    return this.mapToResponse(null, 204);
  }

  // Method for other services to get customers synchronously
  getMockVehicles(): Vehicle[] {
    if (this.vehicles.length === 0) {
      this.generateMockVehicles();
      this.saveVehiclesToStorage();
    }
    return [...this.vehicles]; // Return a copy to prevent external modification
  }

  private loadVehiclesFromStorage(): void {
    try {
      const storedVehicles = localStorage.getItem(this.STORAGE_KEY);
      if (storedVehicles) {
        this.vehicles = JSON.parse(storedVehicles);
        // Migrate vehicles that were stored before the id field was introduced
        let needsSave = false;
        this.vehicles.forEach((v, index) => {
          if (!v.id) {
            v.id = index + 1;
            needsSave = true;
          }
        });
        if (needsSave) {
          this.saveVehiclesToStorage();
        }
        console.log(`Loaded ${this.vehicles.length} vehicles from localStorage`);
      }
    } catch (error) {
      console.error('Error loading vehicles from localStorage:', error);
      this.vehicles = [];
    }
  }

  private saveVehiclesToStorage(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.vehicles));
      console.log(`Saved ${this.vehicles.length} vehicles to localStorage`);
    } catch (error) {
      console.error('Error saving vehicles to localStorage:', error);
    }
  }

  private mapToResponse<T>(res: T, statusCode: number): Observable<HttpEvent<T>> {
    return of(
      new HttpResponse({
        status: statusCode,
        body: res,
      })
    ).pipe(
      delay(500) // Simulate network delay
    );
  }

  private generateMockVehicles() {
    // Generate mock vehicles
    for (let i = 1; i <= 30; i++) {
      this.vehicles.push(this.newVehicle(i));
    }
  }

  private newVehicle(vehicleId: number): Vehicle {
    const makes = [
      'Toyota',
      'Ford',
      'Holden',
      'Mazda',
      'Subaru',
      'Nissan',
      'Honda',
      'BMW',
      'Mercedes-Benz',
      'Audi',
    ];
    const models = {
      Toyota: ['Corolla', 'Camry', 'Hilux', 'RAV4', 'Prius'],
      Ford: ['Ranger', 'Territory', 'Focus', 'Mondeo', 'Mustang'],
      Holden: ['Commodore', 'Cruze', 'Captiva', 'Barina', 'Colorado'],
      Mazda: ['Mazda3', 'CX-5', 'MX-5', 'BT-50', 'Mazda6'],
      Subaru: ['Outback', 'Forester', 'Legacy', 'Impreza', 'XV'],
      Nissan: ['Navara', 'X-Trail', 'Qashqai', 'Micra', 'Pathfinder'],
      Honda: ['Civic', 'Accord', 'CR-V', 'Jazz', 'Odyssey'],
      BMW: ['3 Series', '5 Series', 'X3', 'X5', '1 Series'],
      'Mercedes-Benz': ['C-Class', 'E-Class', 'GLC', 'A-Class', 'S-Class'],
      Audi: ['A4', 'Q5', 'A3', 'Q7', 'A6'],
    };
    const colors = [
      'White',
      'Black',
      'Silver',
      'Blue',
      'Red',
      'Grey',
      'Green',
      'Yellow',
      'Orange',
      'Purple',
    ];

    const make = faker.helpers.arrayElement(makes);
    const model = faker.helpers.arrayElement(models[make as keyof typeof models]);
    const year = faker.number.int({ min: 2010, max: 2024 }).toString();

    // Get a random customer from existing mock customers
    const randomCustomer = this.getRandomCustomer();

    return {
      id: vehicleId,
      tenantId: 1,
      rego: this.generateNZLicensePlate(),
      make,
      model,
      year,
      vin: this.generateVIN(),
      color: faker.helpers.arrayElement(colors),
      customerId: randomCustomer?.id,
      customerName: randomCustomer?.name,
      createdAt: faker.date.past().toISOString(),
      updatedAt: faker.date.past().toISOString(),
      status: 'Active',
    };
  }

  private getRandomCustomer() {
    // Get mock customers synchronously from the customer service
    const customers = this.customerMocksService.getMockCustomers();

    if (customers.length > 0) {
      return faker.helpers.arrayElement(customers);
    }

    // Fallback: if no customers available, return some basic customer info
    return {
      id: faker.number.int({ min: 1, max: 25 }),
      name: faker.company.name(),
    };
  }

  // Helper method to generate NZ license plates
  private generateNZLicensePlate(): string {
    const letters = faker.string.alpha({ length: 3, casing: 'upper' });
    const numbers = faker.string.numeric(3);
    return `${letters}${numbers}`;
  }

  // Helper method to generate VIN numbers
  private generateVIN(): string {
    return faker.string.alphanumeric({ length: 17, casing: 'upper' });
  }
}

const anyQueryMatch = (vehicle: Vehicle, query: string): boolean => {
  if (!query?.trim()) {
    return true;
  }

  const search = query.toLowerCase().trim();
  const isLicensePlateMatch = vehicle.rego?.toLowerCase().includes(search) ?? false;
  const isMakeMatch = vehicle.make?.toLowerCase().includes(search);
  const isModelMatch = vehicle.model?.toLowerCase().includes(search);
  const isYearMatch = vehicle.year?.toLowerCase().includes(search);
  const isVinMatch = vehicle.vin?.toLowerCase().includes(search) ?? false;
  const isColorMatch = vehicle.color?.toLowerCase().includes(search) ?? false;
  const isCustomerNameMatch = vehicle.customerName?.toLowerCase().includes(search) ?? false;

  return (
    isLicensePlateMatch ||
    isMakeMatch ||
    isModelMatch ||
    isYearMatch ||
    isVinMatch ||
    isColorMatch ||
    isCustomerNameMatch
  );
};
